import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/apiClient';

export interface ManagedUser {
  id: number;
  email: string;
  display_name?: string | null;
  role: 'admin' | 'editor';
  is_main: boolean;
  created_at?: number | null;
  last_login?: number | null;
}

export interface PublishRequest {
  id: number;
  template: string;
  requested_at: number;
  draft_version_id: number | null;
  status: string;
  requested_by_email: string;
}

export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: () => apiRequest<{ users: ManagedUser[] }>('/api/users'),
    staleTime: 30_000,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      email: string;
      password: string;
      role: 'admin' | 'editor';
      display_name?: string;
    }) => apiRequest('/api/users', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiRequest(`/api/users/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function useSetRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; role: 'admin' | 'editor' }) =>
      apiRequest(`/api/users/${v.id}`, {
        method: 'PATCH',
        body: { role: v.role },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export function usePublishRequests(enabled: boolean) {
  return useQuery({
    queryKey: ['publish-requests'],
    queryFn: () =>
      apiRequest<{ requests: PublishRequest[] }>('/api/publish-requests'),
    enabled,
    staleTime: 15_000,
    refetchInterval: enabled ? 30_000 : false,
  });
}

export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await apiRequest(`/api/publish-requests/${id}/approve`, {
        method: 'POST',
      });
      return res;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['publish-requests'] }),
  });
}

export function useRejectRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: number; note?: string }) =>
      apiRequest(`/api/publish-requests/${v.id}/reject`, {
        method: 'POST',
        body: { note: v.note },
      }),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['publish-requests'] }),
  });
}
